/**
 * Piezas de los mensajes del asistente. Los textos se guardan como clave i18n + valores
 * (no como frases ya traducidas), así la conversación se muestra en el idioma activo.
 * El contenido del diario y de las notas no se copia en la conversación: se guarda su id
 * y la interfaz lo lee de los datos vigentes (si se borró, ya no aparece).
 */
import { z } from 'zod';
import { changesSchema, intentSchema } from './intents';
import { proposalSchema } from './interpreter';

export const textRefSchema = z.object({
  key: z.string().min(1).max(120),
  params: z.record(z.union([z.string().max(400), z.number()])).default({}),
});
export type TextRef = z.infer<typeof textRefSchema>;

export const tr = (key: string, params: Record<string, string | number> = {}): TextRef => ({ key, params });

export const partSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('text'), text: textRefSchema }),
  /** `plain`: versión en texto de una tarjeta del mismo mensaje (para canales sin tarjetas). */
  z.object({ t: z.literal('list'), title: textRefSchema.nullable(), items: z.array(textRefSchema).max(40), plain: z.boolean().optional() }),
  /** Período, datos utilizados y advertencias: se muestran en letra secundaria. */
  z.object({ t: z.literal('caption'), text: textRefSchema }),
  z.object({ t: z.literal('proposal'), id: z.string().max(64) }),
  z.object({ t: z.literal('notes'), ids: z.array(z.string().max(64)).max(10) }),
  z.object({ t: z.literal('journal'), ids: z.array(z.string().max(64)).max(5) }),
  /** Tarjetas: se dibujan con los datos vigentes (no con una copia del momento de la respuesta). */
  z.object({ t: z.literal('agenda'), from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) }),
  z.object({ t: z.literal('habits'), ids: z.array(z.string().max(64)).max(12) }),
  z.object({ t: z.literal('tasks'), ids: z.array(z.string().max(64)).max(15) }),
]);
export type Part = z.infer<typeof partSchema>;

export const actionSchema = z.discriminatedUnion('type', [
  /** Enviar este texto como si lo hubiera escrito el usuario. */
  z.object({ type: z.literal('say'), text: z.string().min(1).max(500) }),
  /** Seguir con esta interpretación (tras preguntar si era correcta). */
  z.object({ type: z.literal('intent'), intent: intentSchema }),
  /** Mostrar esta propuesta (opciones de una aclaración). */
  z.object({ type: z.literal('propose'), items: z.array(proposalSchema).min(1).max(30) }),
  /** Aplicar este valor a la propuesta en curso (hora, días, duración…). */
  z.object({ type: z.literal('changes'), changes: changesSchema }),
  /** Abrir una pantalla; `agenda` abre Hoy en la vista Agenda. */
  z.object({ type: z.literal('navigate'), to: z.string().regex(/^\/[\w/-]*$/).max(60), agenda: z.boolean().optional() }),
  z.object({ type: z.literal('rephrase') }),
  /** Dejar en la propuesta en curso solo los primeros `count` hábitos nuevos, para no superar el plan. */
  z.object({ type: z.literal('keepNew'), count: z.number().int().min(1).max(30) }),
]);
export type Action = z.infer<typeof actionSchema>;

export const choiceSchema = z.object({ id: z.string().max(64), label: textRefSchema, action: actionSchema });
export type Choice = z.infer<typeof choiceSchema>;

export const messageSchema = z.object({
  id: z.string().max(64),
  role: z.enum(['user', 'assistant']),
  at: z.number(),
  /** Qué es el mensaje: consulta respondida, pregunta, propuesta, resultado de una ejecución… */
  kind: z.enum(['user', 'answer', 'question', 'proposal', 'result', 'error', 'info']),
  /** Solo en mensajes del usuario. */
  text: z.string().max(500).optional(),
  parts: z.array(partSchema).max(12).default([]),
  choices: z.array(choiceSchema).max(8).default([]),
});
export type Message = z.infer<typeof messageSchema>;
